import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Bell, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import NotificationAnalytics from "./NotificationAnalytics";

export default function PushNotificationsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [betaOnly, setBetaOnly] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications/stats`);
      setStats(data);
      setLastRefreshedAt(Date.now());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body required");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/send`, { title, body, beta_only: betaOnly });
      toast.success(`Sent to ${data.sent} users (${data.skipped} skipped)`);
      setTitle(""); setBody("");
      const { data: fresh } = await axios.get(`${API}/admin/notifications/stats`);
      setStats(fresh);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="push-notifications-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={loadStats} lastRefreshedAt={lastRefreshedAt} testId="push-refresh" /></div>
      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <p className="text-xl font-black text-amber-700">{stats?.total_with_token || 0}</p>
          <p className="text-[10px] font-semibold text-amber-500">Subscribed</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-xl font-black text-emerald-700">{stats?.total_enabled || 0}</p>
          <p className="text-[10px] font-semibold text-emerald-500">Enabled</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
          <p className="text-xl font-black text-slate-700">{stats?.total_users || 0}</p>
          <p className="text-[10px] font-semibold text-slate-500">Total Users</p>
        </div>
      </div>

      {/* Manual Send Form */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
        <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Manual Broadcast</p>
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Notification title"
          className="w-full px-3 py-2 bg-white rounded-lg text-sm border border-slate-200 outline-none focus:border-amber-400"
          data-testid="notif-broadcast-title"
        />
        <textarea
          value={body} onChange={e => setBody(e.target.value)}
          placeholder="Notification body"
          rows={2}
          className="w-full px-3 py-2 bg-white rounded-lg text-sm border border-slate-200 outline-none focus:border-amber-400 resize-none"
          data-testid="notif-broadcast-body"
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={betaOnly} onChange={e => setBetaOnly(e.target.checked)} className="rounded" data-testid="notif-beta-only-toggle" />
            Beta testers only
          </label>
          <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition disabled:opacity-50 flex items-center gap-1.5" data-testid="notif-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
        </div>
      </div>

      {/* Recent Sends */}
      {stats?.recent_sends?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-slate-500 mb-2">Recent Sends</p>
          <div className="space-y-1.5">
            {stats.recent_sends.map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-white rounded-lg border border-slate-100 px-3 py-2" data-testid={`notif-log-${i}`}>
                <Bell className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{s.title}</p>
                  <p className="text-[10px] text-slate-400">{s.pref_key} — {s.sent_at ? new Date(s.sent_at).toLocaleString() : "—"}</p>
                </div>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${s.success ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                  {s.success ? "OK" : "FAIL"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Section */}
      <NotificationAnalytics />
    </div>
  );
}
